import { useCallback, useState } from "react";
import {
  listGoalTemplatesReq,
  createGoalTemplateReq,
  updateGoalTemplateReq,
  archiveGoalTemplateReq,
} from "services/api";

const parseErrorMessage = (err, fallbackMessage) =>
  err?.response?.data?.message ||
  (err?.response?.data?.errors && err.response.data.errors[0]?.msg) ||
  fallbackMessage;

function useGoalTemplates(adminId) {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchTemplates = useCallback(async () => {
    if (!adminId) return;
    setIsLoading(true);
    try {
      const { data } = await listGoalTemplatesReq({ admin_id: adminId });
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, "Failed to load goal templates."));
    }
    setIsLoading(false);
  }, [adminId]);

  const createTemplate = async (body) => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      await createGoalTemplateReq({ admin_id: adminId, body });
      setSuccessMessage("Goal template created successfully.");
      await fetchTemplates();
      return true;
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, "Failed to create goal template."));
      return false;
    }
  };

  const updateTemplate = async (id, body) => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      await updateGoalTemplateReq({ admin_id: adminId, id, body });
      setSuccessMessage("Goal template updated successfully.");
      await fetchTemplates();
      return true;
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, "Failed to update goal template."));
      return false;
    }
  };

  const archiveTemplate = async (id) => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      await archiveGoalTemplateReq({ admin_id: adminId, id });
      setSuccessMessage("Goal template archived successfully.");
      await fetchTemplates();
      return true;
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, "Failed to archive goal template."));
      return false;
    }
  };

  return {
    templates,
    isLoading,
    errorMessage,
    successMessage,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    archiveTemplate,
    clearError: () => setErrorMessage(null),
    clearSuccess: () => setSuccessMessage(null),
    setErrorMessage,
  };
}

export default useGoalTemplates;
